import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Stethoscope, ArrowRight, Star, Buildings, CheckCircle } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { api } from "@/lib/api";
import LocationSelect from "@/components/LocationSelect";
import Footer from "@/components/Footer";
import { loadManagedSpecialtyNames } from "@/lib/specialties";
import { InlineLoader } from "@/components/LoadingSkeleton";

export default function DoctorsList() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState([]);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const specialty = params.get("specialty") || "";
  const state = params.get("state") || "";
  const district = params.get("district") || "";

  const load = async () => {
    setLoading(true);
    const search = new URLSearchParams();
    if (q) search.set("q", q);
    if (specialty) search.set("specialty", specialty);
    if (state) search.set("state", state);
    if (district) search.set("district", district);
    try {
      const { data } = await api.get(`/doctors?${search.toString()}`);
      setDoctors(data.doctors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadManagedSpecialtyNames().then(setSpecialties);
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, specialty, state, district]);

  const onSearch = (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const next = new URLSearchParams();
    const term = (f.get("q") || "").toString().trim();
    if (term) next.set("q", term);
    if (specialty) next.set("specialty", specialty);
    if (state) next.set("state", state);
    if (district) next.set("district", district);
    setParams(next);
  };

  const setSpec = (s) => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (s) next.set("specialty", s);
    if (state) next.set("state", state);
    if (district) next.set("district", district);
    setParams(next);
  };

  const setLoc = (st, di) => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (specialty) next.set("specialty", specialty);
    if (st) next.set("state", st);
    if (di) next.set("district", di);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="doctors-list-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Find a doctor</div>
        <h1 className="font-display font-bold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          Real doctors. Real queues. Skip the wait.
        </h1>
        <p className="mt-3 text-muted-foreground max-w-2xl text-sm sm:text-base">
          Browse verified doctors, see their fee and timings, and book a walk-in token from home.
        </p>

        <form onSubmit={onSearch} className="mt-6 sm:mt-8 grid gap-3 lg:grid-cols-[minmax(260px,1fr)_220px_auto_auto]" data-testid="doctor-search-form">
          <div className="relative min-w-0">
            <MagnifyingGlass size={18} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              name="q"
              defaultValue={q}
              placeholder="Search by name, specialty or clinic"
              className="h-12 pl-11 rounded-full"
              data-testid="doctor-search-input"
            />
          </div>
          <select
            value={specialty}
            onChange={(e) => setSpec(e.target.value)}
            className="h-12 w-full rounded-full border border-border bg-background px-4 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="specialty-dropdown"
          >
            <option value="">All Specialties</option>
            {specialties.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <LocationSelect
            state={state}
            district={district}
            onStateChange={(v) => setLoc(v, "")}
            onDistrictChange={(v) => setLoc(state, v)}
            inline
            className="lg:min-w-[430px]"
          />
          <Button type="submit" className="h-12 w-full px-6 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025] lg:w-auto" data-testid="doctor-search-submit">
            Search
          </Button>
        </form>

        <div className="mt-10">
          {loading ? (
            <InlineLoader message="Loading doctors" />
          ) : doctors.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="empty-doctors">
              No doctors match your search yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="doctors-grid">
              {doctors.map((d) => (
                <DoctorCard key={d.id} doctor={d} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function DoctorCard({ doctor }) {
  const navigate = useNavigate();
  return (
    <article
      className="group rounded-3xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer"
      onClick={() => navigate(`/doctors/${doctor.id}`)}
      data-testid={`doctor-card-${doctor.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-muted">
        {doctor.photo_url ? (
          <img src={doctor.photo_url} alt={doctor.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Stethoscope size={48} weight="duotone" />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-xs uppercase tracking-[0.18em] text-accent">{doctor.specialty}</div>
        <h3 className="font-display font-bold text-foreground text-xl mt-1.5 leading-tight flex items-center gap-1.5">
          {doctor.name}
          {doctor.verified && <CheckCircle size={16} weight="fill" className="text-blue-500 shrink-0" />}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{doctor.qualifications}</p>

        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
          {doctor.show_experience !== false && (
            <Badge variant="secondary" className="rounded-full inline-flex items-center gap-1.5 justify-self-start">
              <Star size={14} weight="duotone" />
              {doctor.years_experience}+ yrs
            </Badge>
          )}
          {doctor.show_fee !== false && (
            <Badge variant="outline" className="rounded-full justify-self-end font-display font-bold">
              ₹{doctor.consultation_fee}
            </Badge>
          )}
        </div>

        <div className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Buildings size={14} weight="duotone" />
          <span className="line-clamp-1">{doctor.clinic_name}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          <span className="line-clamp-1">{doctor.clinic_address}</span>
        </div>

        <div className="mt-5 flex justify-end">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary bg-primary/10 rounded-full px-3 py-1.5 group-hover:bg-primary group-hover:text-primary-foreground group-hover:max-w-[180px] transition-all duration-300 max-w-[36px] overflow-hidden cursor-pointer whitespace-nowrap" data-testid={`book-doctor-${doctor.id}`}>
            <ArrowRight size={16} weight="bold" className="shrink-0" />
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">Book Now</span>
          </span>
        </div>
      </div>
    </article>
  );
}
